import React, { useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import styles from "../../../styles/AccountModals.module.css";
import { BASE_URL } from "../../../utils/constants";
import { handleApiError } from "../../../utils/handleApiError";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "../../../components/button-v1";
import Input from "../../../components/ui/input";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const EditStaffModal = ({
  isOpen,
  onClose,
  staff_info,
  comp_renderer,
  current_render,
}) => {
  const queryClient = useQueryClient();

  const schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, "Name is required.")
      .max(100, "Name must be at most 100 characters."),
    email: z
      .string()
      .trim()
      .min(1, "Email is required.")
      .email("Invalid email.")
      .max(254, "Email must be at most 254 characters."),
    phone_number: z
      .string()
      .trim()
      .min(1, "Phone is required")
      .refine(
        (val) => val.replace(/\D/g, "").length === 10,
        "Phone must be 10 digits."
      ),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: staff_info?.name || "",
      email: staff_info?.email || "",
      phone_number: staff_info?.phone_number || "",
    },
  });

  useEffect(() => {
    reset({
      name: staff_info?.name || "",
      email: staff_info?.email || "",
      phone_number: staff_info?.phone_number || "",
    });
  }, [staff_info, reset]);

  const { mutateAsync: editStaff, isLoading: isEditing } = useMutation({
    mutationFn: async (finalFormData) => {
      const token = localStorage.getItem("authToken");
      const url = `${BASE_URL}/external_providers/${staff_info.id}/`;
      const response = await axios.patch(url, finalFormData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      comp_renderer(!current_render);
      onClose();
      queryClient.invalidateQueries(["external-providers"]);
      toast.success("External Staff Edited successfully.");
    },
    onError: (error) => {
      handleApiError(error);
    },
  });

  const onSubmit = async (values) => {
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const firstError =
        parsed.error.issues[0]?.message || "Please fix the errors.";
      toast.error(firstError);
      return;
    }
    const finalFormData = { ...parsed.data };
    await editStaff(finalFormData);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit External Provider</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            className={`${styles.comp_wrapper} ${
              isEditing || isSubmitting ? styles.loading : ""
            }`}
          >
            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <Input
                      type="text"
                      label="Name"
                      placeholder="Name"
                      name={field.name}
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(e && e.target ? e.target.value : e)
                      }
                      onBlur={field.onBlur}
                      error={errors?.name?.message}
                      required
                    />
                  )}
                />
              </div>
            </div>

            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <Input
                      type="email"
                      label="Email"
                      placeholder="Email"
                      name={field.name}
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(e && e.target ? e.target.value : e)
                      }
                      onBlur={field.onBlur}
                      error={errors?.email?.message}
                      required
                    />
                  )}
                />
              </div>
            </div>

            <div className={styles.input_group}>
              <div className={styles.input_field}>
                <Controller
                  control={control}
                  name="phone_number"
                  render={({ field }) => (
                    <Input
                      type="text"
                      label="Phone"
                      mask={true}
                      maskPattern="(999) 999-9999"
                      placeholder="Phone"
                      name={field.name}
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(e && e.target ? e.target.value : e)
                      }
                      onBlur={field.onBlur}
                      error={errors?.phone_number?.message}
                      className="no-arrows"
                      required
                    />
                  )}
                />
              </div>
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={isEditing || isSubmitting}
          >
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditStaffModal;
